import moment from 'moment'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'

import {
  getAttackStageColor,
  getAttackStageReadable
} from 'components/attacks/utils/attack-stages'
import { isKubernetesAttack } from 'components/attacks/utils/kubernetes'
import AttackTypeIcon from 'components/common/AttackTypeIcon'
import { SmallBadge } from 'components/common/Badge'
import BusyOverlay from 'components/common/BusyOverlay'
import Button, { LinkButton } from 'components/common/Button'
import GlyphIcon from 'components/common/GlyphIcon'
import Text from 'components/common/Text'
import CardWrapper from 'components/dashboard/CardWrapper'
import Icon from 'components/Icon'
import {
  ScenarioCommandIconsList,
  getUniqueScenarioCommands
} from 'components/scenarios/overview/cards/ScenarioAttackTypeList'
import theme from 'theme'
import { getFormattedAttackType } from 'utils/metadata'

import EmptyCard from './EmptyCard'

function ExperimemtRowTitle({
  isScenario,
  uniqueNodeCommands,
  displayName,
  attackType
}) {
  if (isScenario) {
    return (
      <Text as="h5" color="medium">
        {displayName ||
          uniqueNodeCommands.map(getFormattedAttackType).join(' + ')}
      </Text>
    )
  } else {
    return (
      <Text data-test-type as="h5" color="medium">
        {getFormattedAttackType(attackType)}
      </Text>
    )
  }
}

ExperimemtRowTitle.propTypes = {
  isScenario: PropTypes.bool,
  uniqueNodeCommands: PropTypes.array,
  displayName: PropTypes.string,
  attackType: PropTypes.string
}

function RecentExperimentRow({ attack, isKubernetes, isScenario }) {
  const { infraAttack = {} } = attack
  const hasInfraAttack = Boolean(infraAttack?.guid)

  const uniqueNodeCommands = isScenario ? getUniqueScenarioCommands(attack) : []

  const args =
    attack?.attack?.impactDefinition?.cliArgs ??
    attack?.infra_command?.commandArgs?.cliArgs ??
    attack.args ??
    []

  const stageColor = isScenario
    ? attack.state === 'PUBLISHED'
      ? theme.colors.blue[0]
      : theme.colors.green[0]
    : getAttackStageColor(infraAttack?.stage || attack.stage)

  const date = isScenario
    ? moment(attack.last_run_at)
    : isKubernetes
    ? moment(infraAttack?.end_time ?? attack?.createdAt ?? 'Error')
    : moment(attack?.start_time ?? attack?.end_time ?? 'Error')

  const formattedDate = date.isValid()
    ? isScenario
      ? date.format('M/D/YYYY')
      : date.format('M/D/YYYY h:mm a')
    : ''

  return (
    <Link
      data-test-recent-experiment-row
      css={{
        display: 'grid',
        columnGap: theme.space[3],
        gridTemplateColumns: isScenario ? '1fr 1fr 42px' : '42px 1fr 1fr 42px',
        alignItems: 'center',
        gap: theme.space[3],
        padding: theme.space[3],
        paddingLeft: theme.space[3],
        borderBottom: theme.border.base,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.colors.panel[0]
        }
      }}
      to={
        isKubernetes
          ? {
              pathname: `/attacks/kubernetes/${attack.uid}`,
              state: {
                k8Attack: attack,
                infraAttack: attack.infraAttack
              }
            }
          : isScenario
          ? `/scenarios/detail/${attack.guid}`
          : {
              pathname: `/attacks/${attack.guid}`,
              state: { infraAttack: attack }
            }
      }
    >
      {isScenario ? null : (
        <GlyphIcon>
          <AttackTypeIcon icon={args[0]} />
        </GlyphIcon>
      )}
      <div
        css={{
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
        <ExperimemtRowTitle
          attackType={args[0]}
          displayName={attack.name}
          isScenario={isScenario}
          uniqueNodeCommands={uniqueNodeCommands}
        />
        <Text
          data-test-absolute-date
          as="small"
          color="normal"
          css={{ marginTop: theme.space[1] }}
          weight="normal"
        >
          {isScenario ? 'Last run: ' : ''}
          {formattedDate}
        </Text>
      </div>
      <div>
        {isScenario ? (
          uniqueNodeCommands.length < 1 ? null : (
            <ScenarioCommandIconsList
              fontSize={theme.space[3]}
              iconSize={theme.space[4]}
              uniqueNodeCommands={uniqueNodeCommands}
            />
          )
        ) : (
          <SmallBadge
            css={{
              backgroundColor: stageColor
            }}
          >
            {isScenario
              ? attack.state
              : getAttackStageReadable(
                  hasInfraAttack ? infraAttack.stage : attack.stage
                )}
          </SmallBadge>
        )}
      </div>
      <Icon
        css={{
          marginLeft: theme.space[2],
          color: theme.colors.steel[2]
        }}
        icon="chevronRight"
      />
    </Link>
  )
}

RecentExperimentRow.propTypes = {
  attack: PropTypes.object,
  isKubernetes: PropTypes.bool,
  isScenario: PropTypes.bool
}

export function RecentExperiments({
  allAttacks,
  attacksLoading,
  isScenario,
  displayViewAll
}) {
  if (attacksLoading) return <BusyOverlay rounded shown />
  if (allAttacks.length === 0) {
    return (
      <EmptyCard title="No recent experiment found">
        <LinkButton label="Create new attack" to="/attacks/new/" />
      </EmptyCard>
    )
  }
  return (
    <div
      css={{
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <div>
        {allAttacks.slice(0, 10).map((attack, i) => {
          return (
            <RecentExperimentRow
              key={`recent-experiment-row-${i}`}
              attack={attack}
              isKubernetes={isKubernetesAttack(attack)}
              isScenario={isScenario}
            />
          )
        })}
      </div>
      {(allAttacks.length > 10 || displayViewAll) && (
        <div
          css={{
            display: 'flex',
            justifyContent: 'center',
            padding: theme.space[2],
            width: '100%'
          }}
        >
          <Button
            secondary
            as={Link}
            to={isScenario ? '/scenarios' : '/attacks'}
          >
            View All
          </Button>
        </div>
      )}
    </div>
  )
}

RecentExperiments.propTypes = {
  filter: PropTypes.string,
  allAttacks: PropTypes.array,
  attacksLoading: PropTypes.bool,
  isScenario: PropTypes.bool,
  displayViewAll: PropTypes.bool
}

export default function RecentExperimentsCard({ allAttacks, attacksLoading }) {
  return (
    <CardWrapper
      hideFilter={true}
      renderCardContent={() => (
        <RecentExperiments
          allAttacks={allAttacks}
          attacksLoading={attacksLoading}
        />
      )}
      title="Recent Attacks"
    />
  )
}

RecentExperimentsCard.propTypes = {
  allAttacks: PropTypes.array,
  attacksLoading: PropTypes.bool
}
